import React, {useState} from 'react';

import {Button, CheckIcon, ModalPopup} from '@kit';

import {parseDice, formatDice} from '../../../model/dice';
import {damageTypeLabel, saveLabel} from '../../../model/labels';
import type {ProcedureOption} from '../../../model/procedures';
import {RollButton} from '../../dice-roller';
import {useFeatureAction} from '../use-feature-action';
import type {ProcedureModalProps} from './procedure-modal-props';

import css from '../../sheet.module.css';

/** выбор эффекта при использовании: Божественная искра или Изгнание нежити */
export const OptionsModal: React.FC<ProcedureModalProps<'options'>> = ({action, procedure, onClose}) => {
	const {spend, canUse} = useFeatureAction(action);
	const [usedOption, setUsedOption] = useState<string | undefined>();

	const use = (option: ProcedureOption) => {
		if (usedOption) return;
		setUsedOption(option.title);
		spend(option.title);
	};

	return (
		<ModalPopup open onClose={onClose} header={action.title} maxWidth={560}>
			{!canUse && !usedOption && <p className={css.muted}>Использований не осталось.</p>}
			<ul className={css.rows}>
				{procedure.options.map((option) => (
					<li key={option.title} className={css.row}>
						<div className={css.rowMain}>
							<span className={css.rowTitle}>
								{option.title}
								{option.save && (
									<span className={css.muted}>
										{' '}
										· {saveLabel([option.save.ability], option.save.dc)}
									</span>
								)}
							</span>
							<OptionButtons
								option={option}
								actionTitle={action.title}
								disabled={(!canUse && !usedOption) || (!!usedOption && usedOption !== option.title)}
								onUse={() => use(option)}
							/>
						</div>
					</li>
				))}
			</ul>
			<div className={css.modalButtons}>
				<Button appearance="primary" icon={<CheckIcon />} onClick={onClose}>
					Готово
				</Button>
			</div>
		</ModalPopup>
	);
};

type OptionButtonsProps = {
	option: ProcedureOption;
	actionTitle: string;
	disabled: boolean;
	/** первое применение эффекта тратит использование */
	onUse(): void;
};

const OptionButtons: React.FC<OptionButtonsProps> = ({option, actionTitle, disabled, onUse}) => {
	const formula = option.formula ? parseDice(option.formula) : undefined;
	if (!formula) {
		return (
			<Button size="small" disabled={disabled} onClick={onUse}>
				Применить
			</Button>
		);
	}
	if (disabled) return <span className={css.muted}>{formatDice(formula)}</span>;
	const damageTypes = option.damageTypes.map((type) => damageTypeLabel(type).toLowerCase()).join(' или ');
	return (
		<span className={css.cellButtons}>
			{option.healing && (
				<RollButton
					title={`${actionTitle}: ${option.title}, лечение`}
					formula={formula}
					appearance="secondary"
					size="small"
					onRoll={onUse}
				>
					{formatDice(formula)} лечение
				</RollButton>
			)}
			{damageTypes && (
				<RollButton
					title={`${actionTitle}: ${option.title}, урон`}
					formula={formula}
					appearance="secondary"
					size="small"
					onRoll={onUse}
				>
					{formatDice(formula)} {damageTypes}
				</RollButton>
			)}
		</span>
	);
};
